import { Components, chipClasses as MuiChipClasses, Theme } from '@mui/material';
import { chipClasses } from './Chip';

export const CHIP_SMALL_HEIGHT = '20px';

export const MuiChip: Components<Theme>['MuiChip'] = {
  defaultProps: {
    variant: 'filled',
    size: 'small',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape.inlineRadius,
    }),
    sizeSmall: {
      height: CHIP_SMALL_HEIGHT,
    },
    deleteIcon: ({ theme }) => ({
      padding: '2px',
      color: theme.featureFlags.panwRebrandStubYear ? theme.palette.text.primary : theme.palette.text.secondary,
    }),
    icon: ({ theme }) => ({
      color: theme.palette.text.primary,
      width: '12px',
      height: '12px',
    }),
    iconSmall: ({ theme }) => ({
      marginLeft: theme.spacing(1),
    }),
    labelSmall: {
      fontSize: '12px',
    },
    // Represents default static chips, clickable chips, and selected selectable chips
    filled: ({ theme }) => {
      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          backgroundColor: theme.palette.chip.clickable.default.background,
          border: `1px solid ${theme.palette.chip.clickable.default.border}`,
          color: theme.palette.chip.clickable.default.text,
          '&:hover, &:focus': {
            backgroundColor: theme.palette.chip.clickable.default.hover,
          },
          [`&.${MuiChipClasses.disabled}`]: {
            opacity: 1,
            color: theme.palette.chip.clickable.disabled.text,
            [`& .${MuiChipClasses.deleteIcon}`]: {
              color: theme.palette.text.disabled,
            },
            [`& .${MuiChipClasses.icon}`]: {
              color: theme.palette.text.disabled,
            },
          },
          // Selectable chip in selected state
          [`&.${chipClasses.selectable}`]: {
            backgroundColor: theme.palette.chip.selectable.selected.background,
            border: `1px solid ${theme.palette.chip.selectable.selected.border}`,
            color: theme.palette.chip.selectable.selected.text,
            '&:hover, &:focus': {
              backgroundColor: theme.palette.chip.selectable.selected.hover,
            },
            [`& .${MuiChipClasses.icon}`]: {
              color: theme.palette.chip.selectable.selected.text,
            },
            [`&.${MuiChipClasses.disabled}`]: {
              opacity: 1,
              backgroundColor: theme.palette.chip.selectable.selectedDisabled.background,
              color: theme.palette.chip.selectable.selectedDisabled.text,
              [`& .${MuiChipClasses.icon}`]: {
                color: theme.palette.text.disabled,
              },
            },
          },
        };
      }

      const darkModeBackgroundColor =
        theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.status.default.lowContrast;

      return {
        backgroundColor: darkModeBackgroundColor,
        border: `1px solid ${darkModeBackgroundColor}`,
        color: theme.palette.text.primary,
        [`& .${MuiChipClasses.icon}`]: {
          color: theme.palette.text.primary,
        },
      };
    },
    // Represents chips that are selectable but are not currently selected
    outlined: ({ theme }) => {
      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          backgroundColor: 'transparent',
          border: `1px solid ${theme.palette.chip.selectable.unselected.border}`,
          color: theme.palette.chip.selectable.unselected.text,
          '&:hover, &:focus': {
            backgroundColor: theme.palette.chip.selectable.unselected.hover,
          },
          [`& .${MuiChipClasses.icon}`]: {
            color: theme.palette.text.primary,
          },
          [`&.${MuiChipClasses.disabled}`]: {
            opacity: 1,
            border: `1px solid ${theme.palette.chip.selectable.unselectedDisabled.border}`,
            color: theme.palette.chip.selectable.unselectedDisabled.text,
            [`& .${MuiChipClasses.icon}`]: {
              color: theme.palette.text.disabled,
            },
          },
        };
      }

      return {
        backgroundColor: theme.palette.background.container,
        border: `1px solid ${theme.palette.chip.selectable.unselected.border}`,
        color: theme.palette.text.secondary,
        '&:hover, &:focus': {
          backgroundColor: theme.palette.chip.selectable.unselected.hover,
        },
        [`& .${MuiChipClasses.icon}`]: {
          color: theme.palette.text.secondary,
        },
      };
    },
  },
};
